// applications/dossier types — Zod schemas + inferred types
import { z } from "zod";

/**
 * One choice on a schema field. `{value, label}` — send `value`, display `label`.
 *
 * ⚠️ Intentionally different from the catalogue-browser shape, which is `{code, label}`. Both ship
 * in the same response (`schema.applicant` vs `schema.reference_catalog`), so the difference is a
 * real trap rather than a naming quibble.
 */
export const SchemaOptionSchema = z.object({
  /**
   * ⚠️ NOT always a string. `residency_confirmed` ships real JSON booleans (`true`/`false`), because
   * the field is a `BooleanField` server-side and the server wants the boolean back — a `"true"`
   * string is rejected. Verified against the running stack, where a `z.string()` here failed the
   * parse and blanked the entire dossier.
   *
   * So the control keys off `String(value)` for rendering and sends the ORIGINAL typed value back.
   */
  value: z.union([z.string(), z.boolean(), z.number()]),
  label: z.string(),
});

export type SchemaOption = z.infer<typeof SchemaOptionSchema>;

/**
 * One field of the applicant dossier, as the round froze it.
 *
 * Everything that decides rendering comes from here — label, input type, requiredness, order,
 * options, and the condition that shows or hides it. Nothing is hardcoded client-side, which is what
 * lets an administrator change the dossier without a deploy (US-E3-03).
 */
export const SchemaFieldSchema = z.object({
  /** The stable field KEY (`weekly_commitment_code`), not a UUID — it is the PATCH payload's name. */
  id: z.string(),
  section: z.string(),
  label: z.string(),
  help_text: z.string(),
  /**
   * Not an enum, deliberately. The renderer switches on it, but an unknown type must degrade to a
   * text input rather than throw away the whole step — the failure mode a strict enum produced when
   * the backend grew one new value (see rounds/rules `applicant_step`).
   */
  input_type: z.string(),
  is_required: z.boolean(),
  display_order: z.number().int(),
  /** e.g. `{ required_when_visible: true }`, `{ min, max }`. Loose by design; read per key. */
  validation: z.record(z.string(), z.unknown()).default({}),
  /**
   * e.g. `{ visible_if: { education_level_code: ["other", "OTHER", "autre"] } }`.
   *
   * The value list carries CASE VARIANTS because the server accepts several spellings of the same
   * choice — so matching must be case-insensitive, not an identity check on the first entry.
   */
  conditions: z.record(z.string(), z.unknown()).default({}),
  catalog_category: z.string().nullable().default(null),
  /** `SCORED` | `CONTEXTUAL` | `OPERATIONAL`. Passed through — never shown to the applicant. */
  data_kind: z.string().default("CONTEXTUAL"),
  options: z.array(SchemaOptionSchema).default([]),
});

export type SchemaField = z.infer<typeof SchemaFieldSchema>;

/** One of the four tabs, server-titled and server-ordered. */
export const SchemaSectionSchema = z.object({
  key: z.string(),
  title: z.string(),
  fields: z.array(SchemaFieldSchema),
});

export type SchemaSection = z.infer<typeof SchemaSectionSchema>;

/** One question this round's administrator authored, placed on a tab by `applicant_step`. */
export const SchemaQuestionSchema = z.object({
  id: z.string(),
  question_text: z.string(),
  help_text: z.string(),
  input_type: z.string(),
  is_contextual: z.boolean().default(false),
  competencies: z
    .array(
      z.object({
        label: z.string(),
        display_order: z.number().int(),
        is_primary: z.boolean().default(false),
      })
    )
    .default([]),
  is_required: z.boolean(),
  options: z.array(SchemaOptionSchema).default([]),
});

export type SchemaQuestion = z.infer<typeof SchemaQuestionSchema>;

/** A group of authored questions, with the title the administrator gave it. */
export const SchemaFormSectionSchema = z.object({
  id: z.string(),
  title: z.string(),
  /** Lower-cased on read: this half of the payload says `FOUNDER`, the other says `founder`. */
  applicant_step: z
    .string()
    .default("FOUNDER")
    .transform((value) => value.toLowerCase()),
  questions: z.array(SchemaQuestionSchema),
});

/**
 * One document the round requires, as the applicant's own payload describes it.
 *
 * Already localized and already filtered to the active requirements server-side, so the panel
 * renders it as-is. `max_files` is parsed but unused until uploading exists.
 */
export const DocumentRequirementSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().default(""),
  is_required: z.boolean(),
  /** Lower-case, no dot ("pdf") — uppercased for display only. */
  accepted_extensions: z.array(z.string()).default([]),
  /** Sent alongside `accepted_extensions` — `start_document_upload` checks BOTH server-side, so a file picked past the client's own `<input accept>` filter still needs this to explain a rejection honestly. */
  accepted_mime_types: z.array(z.string()).default([]),
  max_size_bytes: z.number().int(),
  max_files: z.number().int().default(1),
});

export type DocumentRequirement = z.infer<typeof DocumentRequirementSchema>;

/**
 * One document already attached to the dossier — `GET /applications/{id}/` returns these under
 * `documents[]`. `id` here is genuinely the DOCUMENT's id (the backend key names it `id` in this
 * one response, `document_id` everywhere else — verified against `serialize_application`).
 */
export const ApplicationDocumentSchema = z.object({
  id: z.string(),
  requirement_id: z.string(),
  filename: z.string(),
  mime_type: z.string(),
  size_bytes: z.number().int(),
  /** `PENDING` while the file is mid-upload, `AVAILABLE` once the server verified it landed intact, `REJECTED` if the integrity check failed. Never `DELETED` — those rows are filtered out server-side. */
  status: z.string(),
  upload_expires_at: z.string(),
});

export type ApplicationDocumentRow = z.infer<typeof ApplicationDocumentSchema>;

// Upload-sequence schemas (`UploadIntentSchema`/`DocumentCompleteSchema`) moved to
// `@modules/applications/_shared` — `respond` needs the exact same wire shapes. Re-exported here
// so nothing importing these from `dossier`'s own barrel has to change.
export {
  UploadIntentSchema,
  DocumentCompleteSchema,
  type UploadIntent,
  type DocumentComplete,
} from "@modules/applications/_shared";

/** Per-tab completion, straight from the server. Never re-derived client-side. */
export const SectionProgressSchema = z.object({
  key: z.string(),
  complete_percent: z.number(),
  missing_count: z.number().int(),
});

export type SectionProgress = z.infer<typeof SectionProgressSchema>;

export const ProgressSchema = z.object({
  complete: z.boolean(),
  missing_count: z.number().int(),
  sections: z.array(SectionProgressSchema).default([]),
});

export type Progress = z.infer<typeof ProgressSchema>;

/**
 * An amber, applicant-safe note. **It never blocks anything.**
 *
 * `failed_gates_behavior` is hard-coded `human_review_only` server-side, and US-E3-04's core rule is
 * that no answer a candidat gives may prevent them from submitting. So a warning must never disable
 * a control, gate the Next button, or be styled as an error.
 *
 * ⚠️ Carries `code` and `section` but NO field id, so attaching it to the field it concerns is the
 * client's job — see `WARNING_FIELD` in `warnings.ts`.
 */
export const EligibilityWarningSchema = z.object({
  /** `AGE` | `WILAYA` | `RESIDENCY` | `EDUCATION` | … — a small, stable set. */
  code: z.string(),
  section: z.string(),
  message: z.string(),
});

export type EligibilityWarning = z.infer<typeof EligibilityWarningSchema>;

/**
 * One legal agreement the review screen must show before submit (US-E3-10). Only present on
 * `GET /applications/{id}/` for a v2-schema round (`_is_v2` server-side) — checked against a real
 * v2 application, where it returns exactly the four Jira describes: `PRIVACY_POLICY`,
 * `PERSONAL_DATA_PROCESSING`, `INTAKE_TERMS` (all `is_required: true`), and `FUTURE_AI_USE`
 * (`is_required: false` — declining changes nothing about submission, per the story's core rule).
 *
 * `content` is the FROZEN legal text at the round's snapshot time — real production copy is a
 * client input still pending (same gap as `config/acquire/legal-documents.approved.json`); what a
 * dev round returns today is synthetic placeholder text, clearly labelled as such. Render whatever
 * this field holds rather than a shorter mirror, so a copy swap needs no frontend change.
 */
export const ConsentSchema = z.object({
  consent_type: z.string(),
  is_required: z.boolean(),
  accepted: z.boolean(),
  accepted_at: z.string().nullable(),
  accepted_locale: z.string().nullable(),
  legal_version: z.number().int(),
  content_hash: z.string(),
  title: z.string(),
  content: z.string(),
});

export type Consent = z.infer<typeof ConsentSchema>;

/** One thing still missing, with the target that lets the review link straight to it. */
export const MissingItemSchema = z.object({
  category: z.string(),
  section: z.string(),
  field_id: z.string().nullable().default(null),
  code: z.string(),
  label: z.string(),
  navigation_target: z
    .object({
      section: z.string(),
      field_id: z.string().nullable().default(null),
    })
    .nullable()
    .default(null),
});

export type MissingItem = z.infer<typeof MissingItemSchema>;

/**
 * One saved answer to an authored question.
 *
 * `value` is JSON-native and its type follows the question's `input_type` — a string for text, a
 * number for `NUMBER`, a boolean for `CHECKBOX`, an array for `MULTI_SELECT`. Kept `unknown` here
 * and narrowed at the control, because one schema cannot describe all five shapes honestly.
 */
export const AnswerSchema = z.object({
  question_id: z.string(),
  value: z.unknown(),
});

export type Answer = z.infer<typeof AnswerSchema>;

/**
 * One named person on a MULTIPLE-mode team. A real sub-resource (`ApplicationTeamMember` on the
 * backend), with its own `POST|PATCH|DELETE /team/members/{id}/` endpoints — not a schema field.
 *
 * `time_commitment_code` and `capital_distribution_code` are catalogue-backed
 * (`MEMBER_TIME_COMMITMENT` / `CAPITAL_DISTRIBUTION`), but ⚠️ **the applicant-facing API never
 * exposes their option labels.** Every OTHER catalogue field an applicant answers gets its options
 * embedded straight in `schema.applicant.sections[].fields[].options` — the one endpoint that lists
 * option labels by category (`GET /intake-rounds/{id}/reference-catalog/`) is gated by
 * `require_round_reader` (administrator/program_coordinator only; verified against
 * `apps/backend/modules/acquire/services.py`), so an applicant gets `403`. These two codes were
 * simply never given the same treatment. `TeamMembersPanel` renders both as disabled, not guessed —
 * see its own doc comment. The ask for Aymen: embed their labelled options on the Application DTO
 * the same way every other applicant-facing catalogue field already gets them.
 */
export const TeamMemberSchema = z.object({
  id: z.string(),
  name: z.string(),
  role: z.string(),
  time_commitment_code: z.string().nullable().default(null),
  capital_distribution_code: z.string().nullable().default(null),
  /**
   * ⚠️ NOT a JSON number on the wire. `capital_percentage` is a Django `DecimalField`, and
   * `application_services.py` serializes it via `str(item.capital_percentage)` deliberately — a
   * float would lose precision. Verified live: a real dossier with `"10.00"` here threw straight
   * through `ApplicationSchema.parse` and blanked the entire dossier ("Votre dossier n'a pas pu
   * être chargé"), because a plain `z.number()` rejects a string outright. Same shape of trap as
   * `SchemaOptionSchema.value`'s boolean surprise above — accept the wire type, then normalize.
   */
  capital_percentage: z
    .union([z.number(), z.string()])
    .nullable()
    .default(null)
    .transform((value) => (value === null ? null : Number(value))),
  consent_confirmed: z.boolean().default(false),
});

export type TeamMember = z.infer<typeof TeamMemberSchema>;

/**
 * The team tab's saved values. `members` is real and typed; every other key (`mode`,
 * `other_candidature_code`, `owner_rationale`, `named_members_consent_confirmed`,
 * `named_members_consent_at`) is the same flat, loosely-typed bag as `founder`/`project` — those
 * ARE schema fields, so `DossierSectionStep` reads them by id like any other. `.catchall` keeps
 * them passing through without a second, redundant declaration here.
 */
export const TeamProfileSchema = z
  .object({
    members: z.array(TeamMemberSchema).default([]),
  })
  .catchall(z.unknown());

export type TeamProfile = z.infer<typeof TeamProfileSchema>;

/**
 * One applicant dossier, parsed down to the fields THIS step uses.
 *
 * ⚠️ Deliberately a small slice of a large payload. `GET /applications/{id}/` returns the whole
 * rendering contract — four profiles, the localized schema, answers, documents, consents,
 * `missing_items`, `progress`, `eligibility_warnings`. None of it is parsed here, because none of it
 * is rendered yet, and a schema written ahead of the screen that consumes it is a contract nobody
 * has checked. The fields below are the ones the create-and-redirect step genuinely reads.
 *
 * Zod strips unknown keys by default, so the rest of the payload is dropped rather than rejected —
 * which is what makes growing this schema a purely additive change as the four tabs get built.
 *
 * `revision` is here even though nothing writes yet: it is the optimistic-concurrency token every
 * future mutation must echo back as `expected_revision`, and having it typed from the start stops
 * the first autosave inventing its own source for it.
 */
export const ApplicationSchema = z.object({
  id: z.string(),
  intake_round_id: z.string(),
  /**
   * Not an enum. `DRAFT` and `SUBMITTED` are the two values the applicant contract documents, but
   * this step only passes the value through — and a strict enum on a field nothing branches on is
   * exactly what blanked the whole round form when the backend grew one new value
   * (see rounds/rules `applicant_step`).
   */
  status: z.string(),
  revision: z.number().int(),
  /** Assigned at submission; null on every draft. */
  dossier_number: z.string().nullable(),
  /**
   * ⚠️ Same checklist lesson as `practical`/`consents` above — the backend has always sent this
   * (`application_services.py`'s `submit_application`), but it never had a key here, so it always
   * parsed away to nothing. Null on every draft, alongside `dossier_number`.
   */
  submitted_at: z.string().nullable(),
  /**
   * Server clock at response time, and the deadline it must be measured against. Kept because the
   * one thing this step can already get wrong is trusting the visitor's device clock.
   */
  server_now: z.string(),
  closing_at: z.string().nullable(),
  /** Lifecycle capabilities. They do NOT prove completeness or a verified email. */
  can_edit: z.boolean(),
  can_submit: z.boolean(),

  /**
   * The saved values of the founder tab — a FLAT bag keyed by field id, plus a few server-derived
   * extras (`age_at_opening`, `*_label`).
   *
   * Loosely typed on purpose. Which keys exist is decided by the round's frozen schema, not by this
   * file, so restating them here would be a second source of truth that goes stale the first time an
   * administrator changes the dossier. The renderer reads `schema.applicant.sections[].fields[]` and
   * looks each value up by the same key it will PATCH back.
   */
  founder: z.record(z.string(), z.unknown()).default({}),
  /**
   * The project tab's saved values — same flat, loosely-typed bag as `founder`, and for the same
   * reason: which keys exist is the round's to decide, not this file's. Carries a few fields the
   * frozen schema does NOT render (`name`, `description`, `website_url`, `sector_codes`) — checked
   * against `missing_items`, none of them block completion in this round, so `DossierSectionStep`
   * does not render them either. See `field-groups.ts`'s note on the stale integration guide.
   */
  project: z.record(z.string(), z.unknown()).default({}),
  /**
   * The team tab's saved values. Mostly the same flat, loosely-typed bag as `founder`/`project`
   * (`mode`, `other_candidature_code`, `owner_rationale`, `named_members_consent_confirmed`,
   * `named_members_consent_at`) — but `members` is a REAL sub-resource, not a schema field: it has
   * its own CRUD endpoints (`POST|PATCH|DELETE /team/members/{id}/`) and its own identity, so it is
   * the one key here worth typing for real rather than reading through `DossierField`.
   */
  team: TeamProfileSchema.default({ members: [] }),
  /**
   * The practical tab's saved values — same flat, loosely-typed bag as `founder`/`project`
   * (`mobile_phone`, `interview_language_code`, `interview_mode_code`, `internet_quality_code`,
   * `availability_codes`, `travel_assistance`, `accommodation_assistance`,
   * `accessibility_requested`, `accessibility_details`).
   *
   * ⚠️ Missing from this schema for a while after Practical was wired into the foundation shell —
   * Zod strips unknown keys by default, so every practical field silently parsed to `undefined`
   * regardless of what the server actually held. Every PATCH still round-tripped correctly (the
   * write path never touched this file), so the bug looked exactly like "nothing saves": type a
   * value, it appears to vanish on the next load, because the value was never MISSING, it was
   * being discarded on the way in. Verified against the real backend response before and after
   * this fix — same lesson as the top-level doc comment: growing this schema is additive, but
   * only once the key is actually added here.
   */
  practical: z.record(z.string(), z.unknown()).default({}),

  /** The whole rendering contract, localized. Everything on screen comes from here. */
  schema: z.object({
    applicant: z.object({ sections: z.array(SchemaSectionSchema) }),
    form: z.object({ sections: z.array(SchemaFormSectionSchema) }),
    /**
     * `policy` is `CONFIGURED` | `EXPLICITLY_NONE` — a round may deliberately require nothing, and
     * an empty list under `CONFIGURED` is a different fact from that. Defaulted rather than
     * required so a round without the block still renders the rest of the dossier.
     */
    documents: z
      .object({
        policy: z.string().default("CONFIGURED"),
        requirements: z.array(DocumentRequirementSchema).default([]),
      })
      .default({ policy: "CONFIGURED", requirements: [] }),
  }),

  progress: ProgressSchema,
  /** Amber, applicant-safe, and never blocking. See `EligibilityWarningSchema`. */
  eligibility_warnings: z.array(EligibilityWarningSchema).default([]),
  missing_items: z.array(MissingItemSchema).default([]),
  answers: z.array(AnswerSchema).default([]),
  /**
   * Documents attached to the dossier. Real upload landed WITHOUT the on-device compression
   * US-E3-08 asks for (a deliberate, explicit call — the D14 spike that story is blocked on has
   * not run) — so a large phone photo over a slow connection is exactly as slow as the raw file,
   * not the ~20-second reduction the story describes. Each row is parsed for real now
   * (`ApplicationDocumentSchema`), not the placeholder `z.unknown()` this was before.
   */
  documents: z.array(ApplicationDocumentSchema).default([]),
  /**
   * ⚠️ CHECKLIST ITEM, same lesson as `practical` above: this key must exist here or it silently
   * parses to `[]` regardless of what the server sends (Zod strips unknown keys — the exact bug
   * that made every practical value disappear for hours before anyone noticed). Empty on a v1
   * round (`_is_v2` false server-side) — the review screen must not treat that as "nothing to
   * agree to" when the round genuinely has no consent step, only when it does and the array is
   * legitimately empty by construction. See `ConsentSchema`'s own doc comment for the shape.
   */
  consents: z.array(ConsentSchema).default([]),
});

export type Application = z.infer<typeof ApplicationSchema>;

// The step vocabulary now lives in `_shared/types/step.ts` — the espace feature needs it too
// (routing "Reprendre mon dossier" at the right built step), and cross-feature sharing goes
// through `_shared/`, not a direct reach into another feature's `types/`. Re-exported here so
// every existing `@modules/applications/dossier` import site is unaffected.
export {
  DOSSIER_STEPS,
  BUILT_DOSSIER_STEPS,
  isBuiltDossierStep,
  toDossierStep,
  type DossierStep,
} from "@modules/applications/_shared";
